import { useRef, useState } from "preact/hooks";
import { wallTime } from "../demo-v2/clock.js";
import type { ControlCopy, WallCopy } from "./content.js";
import { useInView, useTicker } from "./motion.js";
import { PHOTOS, rotate, type WallPhoto } from "./photos.js";
import { Wall } from "./wall.js";

export type RevealMode = "now" | "end";

const POOL: readonly WallPhoto[] = PHOTOS.slice(-8).reverse();
const LISTED: readonly WallPhoto[] = POOL.slice(0, 4);

export function visibleOnWall(hidden: readonly string[]): WallPhoto[] {
  return POOL.filter((photo) => !hidden.includes(photo.id));
}

export function toggled(hidden: readonly string[], id: string): string[] {
  return hidden.includes(id) ? hidden.filter((item) => item !== id) : [...hidden, id];
}

export interface ControlProps {
  readonly copy: ControlCopy;
  readonly wall: WallCopy;
}

export function Control({ copy, wall }: ControlProps) {
  const [mode, setMode] = useState<RevealMode>("now");
  const [hidden, setHidden] = useState<readonly string[]>([]);
  const root = useRef<HTMLElement>(null);
  const visible = useInView(root);
  const tick = useTicker(4200, visible && mode === "now");
  const shown = visibleOnWall(hidden);
  const order = rotate(shown, tick);
  const [first, ...rest] = order;
  const count = PHOTOS.length - hidden.length;

  return (
    <section class="section ctl is-sunken" id="produto" ref={root}>
      <div class="wrap">
        <div class="ctl-intro">
          <p class="micro">{copy.label}</p>
          <h2 class="section-title serif">{copy.title}</h2>
          <p class="split-body">{copy.body}</p>
        </div>
        <div class="ctl-grid">
          <div class="ctl-card">
            <div class="ctl-head">
              <strong>{copy.event}</strong>
              <span class="ctl-live">{wall.live}</span>
            </div>
            <div class="ctl-group">
              <p class="micro" id="ctl-reveal">
                {copy.reveal}
              </p>
              <div class="ctl-seg" role="radiogroup" aria-labelledby="ctl-reveal">
                {(["now", "end"] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={mode === option}
                    class={mode === option ? "is-on" : undefined}
                    onClick={() => setMode(option)}
                  >
                    {option === "now" ? copy.revealNow : copy.revealEnd}
                  </button>
                ))}
              </div>
              <p class="ctl-hint">{mode === "now" ? copy.revealNowHint : copy.revealEndHint}</p>
            </div>
            <div class="ctl-group">
              <p class="micro">{copy.recent}</p>
              <ul class="ctl-list">
                {LISTED.map((photo) => {
                  const off = hidden.includes(photo.id);
                  return (
                    <li key={photo.id} class={off ? "is-hidden" : undefined}>
                      <img src={photo.small} alt="" width={56} height={70} loading="lazy" />
                      <span class="ctl-who">
                        <strong>{photo.author}</strong>
                        <span>{off ? copy.hidden : wallTime(photo.at)}</span>
                      </span>
                      <button
                        type="button"
                        class="ctl-toggle"
                        aria-label={(off ? copy.showLabel : copy.hideLabel).replace(
                          "{name}",
                          photo.author,
                        )}
                        onClick={() => setHidden((list) => toggled(list, photo.id))}
                      >
                        {off ? copy.show : copy.hide}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
          <div class="ctl-tv">
            <Wall
              copy={wall}
              host={copy.event}
              count={count}
              waiting={mode === "end"}
              featured={first ? { key: first.id, src: first.large, author: first.author } : null}
              recent={rest}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
